import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../contexts/UserContext'
import { useWorkOrderContext } from '../../contexts/WorkOrderContext'
import { projectService } from '../../services/projectService'
import { userService } from '../../services/userService'
import {
  isCurrentUserSender,
  shouldShowAcceptDeclineButtons,
} from '../../utils/fieldVisibility'
import {
  areAllInvitationsAccepted,
  canUserAcceptInvitations,
  getInvitationProgress,
  getInvitationStatusText,
} from '../../utils/invitationUtils'

import { workOrderService } from '../../services/workOrderService'
import type { Project } from '../../types/project'
import type { Contact, UserType } from '../../types/user'
import {
  type WorkOrder,
  type WorkOrderInvite,
  WorkOrderStatus,
} from '../../types/workorder'
import DeclineCommentModal from '../DeclineCommentModal'
import Modal from '../Modal'
import ProjectHeader from '../ProjectHeader'
import AddWorkOrderModal from './AddWorkOrderModal'
import DesignatedDriverForm, {
  type DesignatedDriver,
} from './DesignatedDriverForm'
import DriverSelection from './DriverSelection'
import WorkOrderConfirmation from './WorkOrderConfirmation'

interface AcceptWorkOrderModalProps {
  workOrder: WorkOrder
  onClose: () => void
  onSuccess: () => void
}

const AcceptWorkOrderModal: React.FC<AcceptWorkOrderModalProps> = ({
  workOrder,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    workOrders,
    updateWorkOrderStatus,
    refreshWorkOrders,
    assignDrivers,
    acceptWorkOrderInvite,
    getUserPendingWorkOrderInvitations,
  } = useWorkOrderContext()
  const { user: currentUser } = useUser()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [project, setProject] = useState<Project | null>(null)
  const [selectedDriverIds, setSelectedDriverIds] = useState<number[]>([])
  const [isAssigningDrivers, setIsAssigningDrivers] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [userHasAccepted, setUserHasAccepted] = useState(false)
  const [currentStep, setCurrentStep] = useState<string>('')
  const [progress, setProgress] = useState(0)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false)
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false)
  const [optimisticAcceptedCount, setOptimisticAcceptedCount] = useState<
    number | null
  >(null)

  // Helper to create empty driver
  const createEmptyDriver = (): DesignatedDriver => ({
    id: `driver-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    note: '',
  })

  // State for designated driver form
  const [selectionMode, setSelectionMode] = useState<'existing' | 'new'>(
    'existing',
  )
  const [designatedDrivers, setDesignatedDrivers] = useState<
    DesignatedDriver[]
  >([createEmptyDriver()])
  const [trucksAccepted, setTrucksAccepted] = useState<number>(1)

  // Transform drivers for backend submission
  const transformDriversForBackend = (drivers: DesignatedDriver[]) =>
    drivers.map((d) => ({
      first_name: d.firstName,
      last_name: d.lastName,
      phone_number: d.phoneNumber,
      email_address: d.email,
      note: d.note,
    }))

  // Use the centralized notification context to mark work orders as read
  const { workOrders: workOrderNotifications, refresh: refreshNotifications } =
    useNotificationContext()

  // Function to refresh work order data
  const refreshWorkOrderData = useCallback(async () => {
    try {
      await refreshWorkOrders()

      // Reset optimistic count after refresh to use real data
      setOptimisticAcceptedCount(null)

      // The work order data will be updated in the context
      // We need to get the updated work order from the context
      // This will be handled by the parent component passing updated data
    } catch (error) {
      console.error('Failed to refresh work order data:', error)
    }
  }, [refreshWorkOrders])

  // Get the current work order from context (which gets updated after refresh)
  // or fall back to the original prop if not found
  const currentWorkOrder =
    workOrders.find((wo) => wo.id === workOrder.id) || workOrder

  // Get created_by ID from User object
  const createdById = currentWorkOrder.created_by.id

  // Use the visibility utility to determine if current user is the sender
  const visibilityContext = {
    currentUser: currentUser ?? null,
    senderUserId: createdById,
    senderStakeholderType:
      currentWorkOrder.created_by?.current_profile?.stakeholder_type,
  }
  const isCreator = isCurrentUserSender(visibilityContext)
  const showAcceptDecline = shouldShowAcceptDeclineButtons(visibilityContext)

  // Check if current user can accept invitations
  const canAccept = currentUser?.user_type
    ? canUserAcceptInvitations(currentUser.user_type as UserType)
    : false

  // Get invitation progress data - use optimistic count if available
  const acceptedCount =
    optimisticAcceptedCount !== null
      ? optimisticAcceptedCount
      : currentWorkOrder.total_owner_invites_accepted
  const invitationProgress = getInvitationProgress(
    acceptedCount,
    currentWorkOrder.total_owner_invites_sent,
  )
  const allInvitationsAccepted = areAllInvitationsAccepted(
    acceptedCount,
    currentWorkOrder.total_owner_invites_sent,
  )

  // Check if current user has already accepted their invitation
  const checkUserAcceptanceStatus = useCallback(async () => {
    if (currentUser?.email) {
      // First check the currentWorkOrder.invitations array
      if (currentWorkOrder.invitations) {
        const userInvitation = currentWorkOrder.invitations.find(
          (invite) => invite.email === currentUser.email,
        )
        if (userInvitation?.accepted) {
          return true
        }
      }

      // If not found in currentWorkOrder.invitations, check if we have a pending invitation
      // If there's no pending invitation for this user, they must have accepted
      try {
        const userInvitations = await getUserPendingWorkOrderInvitations()
        const userInvitation = userInvitations.find(
          (invite) => invite.email === currentUser.email,
        )
        // If no pending invitation found for this user, they must have accepted
        return !userInvitation
      } catch (error) {
        console.warn(
          'Failed to check user acceptance status from backend:',
          error,
        )
        return false
      }
    }
    return false
  }, [
    currentWorkOrder.invitations,
    currentUser?.email,
    getUserPendingWorkOrderInvitations,
  ])

  // Update user acceptance status when work order data changes
  useEffect(() => {
    const updateAcceptanceStatus = async () => {
      const hasAccepted = await checkUserAcceptanceStatus()
      setUserHasAccepted(hasAccepted)
    }
    updateAcceptanceStatus()
  }, [checkUserAcceptanceStatus])

  // Periodic refresh when waiting for other parties to accept
  useEffect(() => {
    const isWorkOrderAccepted = currentWorkOrder.status === 'ACCEPTED'
    const shouldRefresh =
      userHasAccepted && !allInvitationsAccepted && !isWorkOrderAccepted

    if (shouldRefresh) {
      const interval = setInterval(async () => {
        await refreshWorkOrderData()
      }, 5000) // Check every 5 seconds

      return () => clearInterval(interval)
    }
  }, [
    userHasAccepted,
    allInvitationsAccepted,
    currentWorkOrder.status,
    refreshWorkOrderData,
  ])

  // Handle navigation when all invitations are accepted OR work order status is ACCEPTED
  useEffect(() => {
    const isWorkOrderAccepted = currentWorkOrder.status === 'ACCEPTED'
    const shouldNavigate =
      (userHasAccepted && allInvitationsAccepted) || isWorkOrderAccepted

    if (shouldNavigate) {
      // Small delay to ensure UI updates are visible
      const timer = setTimeout(() => {
        navigate('/work-orders/accepted')
        onSuccess()
        onClose()
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [
    userHasAccepted,
    allInvitationsAccepted,
    currentWorkOrder.status,
    navigate,
    onSuccess,
    onClose,
  ])

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const contactsData = await userService.getContacts()
        setContacts(contactsData)
      } catch (error) {
        console.error('Failed to fetch contacts:', error)
      }
    }

    fetchContacts()
  }, [])

  useEffect(() => {
    const fetchProject = async () => {
      try {
        // Set project data - either from workOrder.project if it's already an object or fetch it
        if (typeof workOrder.project === 'object') {
          setProject(workOrder.project)
        } else if (typeof workOrder.project === 'number') {
          const projectData = await projectService.getProject(
            workOrder.project.toString(),
          )
          setProject(projectData)
        }
      } catch (error) {
        console.error('Failed to fetch project:', error)
      }
    }

    fetchProject()
  }, [workOrder.project])

  const handleDriverAssignment = async (workOrderId: string) => {
    if (selectedDriverIds.length === 0) return

    setIsAssigningDrivers(true)
    try {
      const response = await assignDrivers(workOrderId, selectedDriverIds)

      if (response.success) {
        console.log('✅ All drivers assigned successfully:', response.message)
      } else {
        // Handle partial success/failure
        const successful = response.results.filter((r) => r.success).length
        const failed = response.results.filter((r) => !r.success).length

        if (successful > 0) {
          console.log(`✅ ${successful} drivers assigned successfully`)
        }
        if (failed > 0) {
          console.warn(`⚠️ ${failed} drivers failed to assign:`)
          for (const result of response.results.filter((r) => !r.success)) {
            console.warn(`- ${result.driver_name}: ${result.error}`)
          }
        }
      }
    } catch (error) {
      console.error('❌ Failed to assign drivers:', error)
      throw error // Re-throw to be handled by caller
    } finally {
      setIsAssigningDrivers(false)
    }
  }

  const handleAccept = async () => {
    setIsSubmitting(true)
    setError(null)
    setProgress(0)

    try {
      if (workOrder.id) {
        // Check if this is a multi-party invitation system
        if (workOrder.invitation_status) {
          // For multi-party invitations, we need to accept the invitation first
          if (!canAccept) {
            throw new Error(
              'Only project managers and administrators can accept work order invitations.',
            )
          }

          // Always get the invitation from the backend to ensure we have the correct ID
          setCurrentStep('Finding your invitation...')
          setProgress(30)

          console.log('🔍 Looking for invitation for user:', currentUser?.email)
          console.log('📋 Work order invitations:', workOrder.invitations)
          console.log('🎯 Attempting to accept work order:', {
            id: workOrder.id,
            label: workOrder.label,
            invitationStatus: workOrder.invitation_status,
          })

          let userInvitation = null
          let userInvitations: WorkOrderInvite[] = []
          try {
            userInvitations = await getUserPendingWorkOrderInvitations()
            console.log('📬 Pending invitations from backend:', userInvitations)

            // Add comprehensive debugging for work order ID matching
            console.log(
              '🔍 Target work order ID:',
              workOrder.id,
              typeof workOrder.id,
            )
            console.log('🔍 Full work order object:', workOrder)
            console.log('📋 All pending invitations:', userInvitations)

            // Log all pending invitations for this user
            const userInvitationsForEmail = userInvitations.filter(
              (invite) => invite.email === currentUser?.email,
            )
            console.log(
              '🎯 Pending invitations for user:',
              userInvitationsForEmail.map((inv) => ({
                id: inv.id,
                workOrderId: inv.workorder?.id,
                workOrderLabel: inv.workorder?.label,
                email: inv.email,
                accepted: inv.accepted,
              })),
            )

            userInvitations.forEach((invite, i) => {
              console.log(`Invite ${i}:`, {
                id: invite.id,
                workOrderId: invite.workorder?.id,
                workOrderIdAlt: invite.workorder_id,
                workOrderIdSnake: invite.work_order_id,
                email: invite.email,
                workOrderIdType: typeof invite.workorder?.id,
                accepted: invite.accepted,
                workOrderLabel: invite.workorder?.label,
              })
            })

            // Find invitation for the specific work order the user clicked on
            userInvitation = userInvitations.find((invite) => {
              // Check all possible property paths for work order ID
              const inviteWorkOrderId =
                invite.workorder?.id ||
                invite.workorder_id ||
                invite.work_order_id
              const targetId = Number.parseInt((workOrder.id || 0).toString())
              const inviteId = Number.parseInt(
                inviteWorkOrderId?.toString() || '0',
              )
              const emailMatch = invite.email === currentUser?.email
              const notAccepted = !invite.accepted
              const workOrderMatch = inviteId === targetId

              console.log(`Checking invitation ${invite.id}:`, {
                inviteWorkOrderId: inviteId,
                targetWorkOrderId: targetId,
                workOrderMatch,
                emailMatch,
                notAccepted,
                inviteLabel: invite.workorder?.label,
                targetLabel: workOrder.label,
              })

              return workOrderMatch && emailMatch && notAccepted
            })
            console.log(
              '👤 Found invitation in pending for work order',
              workOrder.id,
              ':',
              userInvitation,
            )
          } catch (error) {
            console.warn(
              'Failed to get user work order invitations from backend:',
              error,
            )
          }

          // Fallback to work order invitations if backend call fails
          if (!userInvitation) {
            userInvitation = workOrder.invitations?.find(
              (invite) =>
                invite.email === currentUser?.email && !invite.accepted,
            )
            console.log(
              '👤 Fallback - Found invitation in work order:',
              userInvitation,
            )
          }

          if (!userInvitation) {
            console.error(
              '❌ No pending invitation found for this work order:',
              {
                clickedWorkOrderId: workOrder.id,
                clickedWorkOrderLabel: workOrder.label,
                userEmail: currentUser?.email,
                availableInvitations: userInvitations?.map((inv) => ({
                  id: inv.id,
                  workOrderId: inv.workorder?.id,
                  workOrderLabel: inv.workorder?.label,
                  email: inv.email,
                  accepted: inv.accepted,
                })),
              },
            )

            // Check if user has invitations for other dispatches
            const userInvitationsForEmail =
              userInvitations?.filter(
                (inv) => inv.email === currentUser?.email,
              ) || []
            if (userInvitationsForEmail.length > 0) {
              const invitedWorkOrders = userInvitationsForEmail
                .map(
                  (inv) =>
                    `${inv.workorder?.label || 'Unknown'} (ID: ${inv.workorder?.id || 'Unknown'})`,
                )
                .join(', ')

              throw new Error(
                `You have pending invitations for other dispatches (${invitedWorkOrders}), but not for dispatch ${workOrder.label || workOrder.id}. Please check your notifications and click on the correct dispatch.`,
              )
            } else {
              throw new Error(
                `No pending invitation found for dispatch ${workOrder.label || workOrder.id}. Please contact support if you believe this is an error.`,
              )
            }
          }

          // Call the actual invitation acceptance endpoint
          setCurrentStep('Accepting invitation...')
          setProgress(50)
          try {
            console.log('🎯 Attempting to accept invitation:', {
              workOrderId: workOrder.id,
              invitationId: userInvitation.id,
              invitationIdString: userInvitation.id.toString(),
              userEmail: currentUser?.email,
            })

            await acceptWorkOrderInvite(
              workOrder.id,
              userInvitation.id.toString(),
            )

            toast.success('Dispatch invitation accepted successfully!', {
              style: { color: '#31C48D' },
            })

            // Optimistic UI update - immediately show user has accepted
            setUserHasAccepted(true)
            setCurrentStep(
              `Invitation accepted for dispatch ${userInvitation.workorder?.label || workOrder.label}! Updating status...`,
            )
            setProgress(70)

            // Optimistic update of invitation counts
            if (currentWorkOrder.total_owner_invites_accepted !== undefined) {
              const newCount =
                (currentWorkOrder.total_owner_invites_accepted || 0) + 1
              currentWorkOrder.total_owner_invites_accepted = newCount
              setOptimisticAcceptedCount(newCount)
            }

            // Refresh work order data to get updated invitation counts
            await refreshWorkOrderData()
          } catch (inviteError) {
            console.error(
              'Failed to accept work order invitation:',
              inviteError,
            )
            throw new Error('Failed to accept invitation. Please try again.')
          }
        } else {
          // Legacy single-user acceptance
          setCurrentStep('Accepting dispatch...')
          setProgress(50)
          await updateWorkOrderStatus(workOrder.id, WorkOrderStatus.ACCEPTED)

          // If using designated drivers, call the accept endpoint with drivers
          if (selectionMode === 'new' && designatedDrivers.length > 0) {
            setCurrentStep('Submitting designated drivers...')
            setProgress(60)
            try {
              await workOrderService.acceptWithDrivers(workOrder.id, {
                number_of_trucks_accepted: designatedDrivers.length,
                designated_drivers:
                  transformDriversForBackend(designatedDrivers),
              })
              toast.success('Dispatch accepted with designated drivers!', {
                style: { color: '#31C48D' },
              })
            } catch (error) {
              console.error('Failed to submit designated drivers:', error)
              throw new Error('Failed to submit designated drivers.')
            }
          } else {
            toast.success('Dispatch accepted successfully!', {
              style: { color: '#31C48D' },
            })
          }
        }

        // Driver assignment only happens when work order is fully accepted
        if (
          selectedDriverIds.length > 0 &&
          !currentWorkOrder.invitation_status &&
          currentWorkOrder.id
        ) {
          setCurrentStep('Assigning drivers...')
          setProgress(80)
          try {
            await handleDriverAssignment(currentWorkOrder.id)
          } catch (driverAssignmentError) {
            console.error('Driver assignment failed:', driverAssignmentError)
            throw new Error('Failed to assign drivers.')
          }
        }
      }

      setCurrentStep('Finalizing...')
      setProgress(90)

      // Refresh work order data to get updated invitation counts
      await refreshWorkOrderData()

      // Refresh notifications to update counts after status change
      try {
        await refreshNotifications()
      } catch (error) {
        console.error('Failed to refresh notifications:', error)
      }

      setProgress(100)
      setCurrentStep('Complete!')
      setShowSuccess(true)

      // The navigation and modal closing will be handled by useEffect hooks
      // based on the updated invitation status
    } catch (error) {
      console.error('Failed to accept work order:', error)

      // Provide more specific error messages to the user
      let errorMessage = 'Failed to accept work order'
      if (error instanceof Error) {
        errorMessage = error.message
      }

      setError(errorMessage)
      setCurrentStep('')
      setProgress(0)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAcceptClick = () => {
    setShowAcceptConfirmation(true)
  }

  const handleAcceptConfirmationYes = () => {
    setShowAcceptConfirmation(false)
    handleAccept()
  }

  const handleAcceptConfirmationNo = () => {
    setShowAcceptConfirmation(false)
  }

  const handleDeclineClick = () => {
    setShowDeclineConfirmation(true)
  }

  const handleDeclineConfirmationYes = () => {
    setShowDeclineConfirmation(false)
    // Immediately decline without comment
    handleDeclineConfirm('')
  }

  const handleDeclineConfirmationNo = () => {
    setShowDeclineConfirmation(false)
  }

  const handleDeclineConfirmationWithComment = () => {
    setShowDeclineConfirmation(false)
    setShowDeclineModal(true)
  }

  const handleDeclineConfirm = async (comment: string) => {
    setIsSubmitting(true)
    try {
      if (currentWorkOrder.id) {
        await updateWorkOrderStatus(
          currentWorkOrder.id,
          WorkOrderStatus.REJECTED,
          comment || undefined,
        )
      }

      // Mark work order as read when declined (declined work orders are resolved)
      try {
        if (workOrderNotifications?.markAsRead) {
          await workOrderNotifications.markAsRead(Number(currentWorkOrder.id))
        }
      } catch (error) {
        console.error('Failed to mark work order as read:', error)
        // Don't show error to user as this is not critical
      }

      await refreshWorkOrders()
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to decline dispatch:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeclineCancel = () => {
    setShowDeclineModal(false)
  }

  // If current user is the creator, show the edit modal instead
  if (isCreator) {
    return <AddWorkOrderModal onClose={onClose} />
  }

  const isLoading = isSubmitting || isAssigningDrivers

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>Dispatch – Pending</h2>
          <div className={css(styles.poNumber)}>
            PID: {project?.label || 'None'}
          </div>
          {currentWorkOrder.label && (
            <div className={css(styles.woLabel)}>
              WOID: {currentWorkOrder.label}
            </div>
          )}
        </div>
        <div className={css(styles.content)}>
          <WorkOrderConfirmation data={currentWorkOrder} />

          {/* Multi-party Invitation Progress */}
          {currentWorkOrder.invitation_status && (
            <div className={css(styles.invitationSection)}>
              <h3 className={css(styles.sectionTitle)}>Invitation Status</h3>
              <div className={css(styles.invitationStatus)}>
                <div className={css(styles.statusInfo)}>
                  <span className={css(styles.statusLabel)}>
                    {getInvitationStatusText(
                      currentWorkOrder.invitation_status,
                    )}
                  </span>
                  <span className={css(styles.progressText)}>
                    {currentWorkOrder.total_owner_invites_accepted || 0} of{' '}
                    {currentWorkOrder.total_owner_invites_sent || 0} accepted
                  </span>
                </div>
                <div className={css(styles.progressBar)}>
                  <div
                    className={css(styles.progressFill)}
                    style={{ width: `${invitationProgress}%` }}
                  />
                </div>
              </div>
              {!canAccept && (
                <div className={css(styles.warningMessage)}>
                  Only project managers and administrators can accept dispatch
                  invitations.
                </div>
              )}
              {canAccept && !allInvitationsAccepted && (
                <div className={css(styles.infoMessage)}>
                  {userHasAccepted
                    ? 'You have accepted your invitation. Waiting for other parties to accept.'
                    : 'You can accept your invitation now. The dispatch will be fully accepted once all parties accept.'}
                </div>
              )}
            </div>
          )}

          {/* Driver Assignment Section */}
          <div className={css(styles.driverSection)}>
            <h3 className={css(styles.sectionTitle)}>Driver Assignment</h3>

            {/* Selection Mode Toggle */}
            <div className={css(styles.selectionModeToggle)}>
              <button
                type="button"
                onClick={() => setSelectionMode('existing')}
                className={css(
                  styles.modeButton,
                  selectionMode === 'existing' && styles.modeButtonActive,
                )}
                disabled={isLoading}
              >
                Select Existing Drivers
              </button>
              <button
                type="button"
                onClick={() => setSelectionMode('new')}
                className={css(
                  styles.modeButton,
                  selectionMode === 'new' && styles.modeButtonActive,
                )}
                disabled={isLoading}
              >
                Add New Drivers
              </button>
            </div>

            {selectionMode === 'existing' ? (
              <DriverSelection
                selectedDriverIds={selectedDriverIds}
                onDriversChange={setSelectedDriverIds}
                isDisabled={isLoading}
              />
            ) : (
              <DesignatedDriverForm
                drivers={designatedDrivers}
                onChange={setDesignatedDrivers}
                isDisabled={isLoading}
                maxDrivers={20}
                minDrivers={1}
              />
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className={css(styles.errorMessage)}>
              <div className={css(styles.errorContent)}>
                <span>{error}</span>
                <button
                  className={css(styles.retryBtn)}
                  onClick={() => {
                    setError(null)
                    setCurrentStep('')
                    setProgress(0)
                    setShowSuccess(false)
                  }}
                >
                  <i className="fa fa-refresh" style={{ marginRight: '4px' }} />
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Progress Indicator */}
          {isSubmitting && currentStep && (
            <div className={css(styles.progressSection)}>
              <div className={css(styles.progressHeader)}>
                <span className={css(styles.progressStep)}>
                  {showSuccess ? (
                    <>
                      <i
                        className="fa fa-check-circle"
                        style={{ marginRight: '8px', color: '#10B981' }}
                      />
                      {currentStep}
                    </>
                  ) : (
                    <>
                      <i
                        className="fa fa-spinner fa-spin"
                        style={{ marginRight: '8px' }}
                      />
                      {currentStep}
                    </>
                  )}
                </span>
                <span className={css(styles.progressPercent)}>{progress}%</span>
              </div>
              <div className={css(styles.progressBarContainer)}>
                <div
                  className={css(styles.progressBarFill)}
                  style={{
                    width: `${progress}%`,
                    backgroundColor: showSuccess ? '#10B981' : '#2563EB',
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div className={css(styles.footer)}>
          <div className={css(styles.footerLeft)}>
            <button onClick={onClose} className={css(styles.closeBtn)}>
              Close
            </button>
          </div>
          <div className={css(styles.footerRight)}>
            {userHasAccepted ? (
              <button className={css(styles.waitingBtn)} disabled={true}>
                Waiting for others to accept...
              </button>
            ) : (
              <>
                <button
                  onClick={handleDeclineClick}
                  className={css(styles.declineBtn)}
                  disabled={isLoading}
                >
                  Decline
                </button>
                {canAccept ? (
                  <button
                    onClick={handleAcceptClick}
                    className={css(styles.acceptBtn)}
                    disabled={isLoading}
                  >
                    {isSubmitting && (
                      <>
                        <i
                          className="fa fa-spinner fa-spin"
                          style={{ marginRight: '8px' }}
                        />
                        {currentStep || 'Processing...'}
                      </>
                    )}
                    {!isLoading && 'I Agree'}
                  </button>
                ) : (
                  <div className={css(styles.viewOnlyBadge)}>View Only</div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Accept Confirmation Dialog */}
        {showAcceptConfirmation && (
          <div className={css(styles.confirmationOverlay)}>
            <div className={css(styles.confirmationModal)}>
              <div className={css(styles.confirmationHeader)}>
                <h3 className={css(styles.confirmationTitle)}>
                  Confirm Agreement
                </h3>
              </div>
              <div className={css(styles.confirmationContent)}>
                <p className={css(styles.confirmationText)}>
                  By selecting and then clicking on the [I Agree] button I
                  indicate I actually have read the provisions of the Dispatch
                  Terms and I expressly agree to the provisions of the Dispatch
                  Terms.
                </p>
              </div>
              <div className={css(styles.confirmationFooter)}>
                <button
                  onClick={handleAcceptConfirmationNo}
                  className={css(styles.confirmationCancelBtn)}
                >
                  No, I made an error
                </button>
                <button
                  onClick={handleAcceptConfirmationYes}
                  className={css(styles.confirmationAcceptBtn)}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? 'Processing...'
                    : 'Yes, I agree to the Dispatch Terms'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Decline Confirmation Dialog */}
        {showDeclineConfirmation && (
          <div className={css(styles.confirmationOverlay)}>
            <div className={css(styles.confirmationModal)}>
              <div className={css(styles.confirmationHeader)}>
                <h3 className={css(styles.confirmationTitle)}>
                  Confirm Decline
                </h3>
              </div>
              <div className={css(styles.confirmationContent)}>
                <p className={css(styles.confirmationText)}>
                  By selecting and then clicking on the [Decline] button I
                  indicate I expressly decline the provisions of the Dispatch
                  Terms.
                </p>
              </div>
              <div
                className={css(
                  styles.confirmationFooter,
                  styles.confirmationFooterThreeButtons,
                )}
              >
                <button
                  onClick={handleDeclineConfirmationNo}
                  className={css(styles.confirmationCancelBtn)}
                >
                  No, I made an error
                </button>
                <button
                  onClick={handleDeclineConfirmationWithComment}
                  className={css(styles.confirmationCommentBtn)}
                >
                  Decline Dispatch Terms with Comment
                </button>
                <button
                  onClick={handleDeclineConfirmationYes}
                  className={css(styles.confirmationDeclineBtn)}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? 'Processing...'
                    : 'Yes, I decline Dispatch Terms'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Decline Reason Modal */}
        {showDeclineModal && (
          <DeclineCommentModal
            title="Decline Dispatch"
            onConfirm={handleDeclineConfirm}
            onCancel={handleDeclineCancel}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
    position: 'relative',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  content: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  poNumber: {
    fontSize: '12px',
    color: '#6B7280',
    position: 'absolute',
    top: '50px',
    left: '24px',
  },
  woLabel: {
    fontSize: '12px',
    color: '#6B7280',
    position: 'absolute',
    top: '50px',
    right: '24px',
  },
  driverSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  sectionTitle: {
    margin: 0,
    marginBottom: '16px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
  selectionModeToggle: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  modeButton: {
    flex: 1,
    padding: '10px 16px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: '#F9FAFB',
    color: '#374151',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  modeButtonActive: {
    backgroundColor: '#2563EB',
    color: 'white',
    borderColor: '#2563EB',
    ':hover': {
      backgroundColor: '#1D4ED8',
    },
  },
  footer: {
    padding: '24px',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    display: 'flex',
    gap: '8px',
  },
  footerRight: {
    display: 'flex',
    gap: '8px',
  },
  closeBtn: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  declineBtn: {
    backgroundColor: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    marginRight: '8px',
    cursor: 'pointer',
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  acceptBtn: {
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  invitationSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  invitationStatus: {
    marginBottom: '16px',
  },
  statusInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  statusLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
  },
  progressText: {
    fontSize: '12px',
    color: '#6B7280',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#E5E7EB',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#10B981',
    transition: 'width 0.3s ease',
  },
  warningMessage: {
    fontSize: '12px',
    color: '#F59E0B',
    backgroundColor: '#FEF3C7',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '8px',
  },
  infoMessage: {
    fontSize: '12px',
    color: '#2563EB',
    backgroundColor: '#EFF6FF',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '8px',
  },
  errorMessage: {
    fontSize: '12px',
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '16px',
  },
  errorContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  retryBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #DC2626',
    color: '#DC2626',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    ':hover': {
      backgroundColor: '#DC2626',
      color: 'white',
    },
  },
  progressSection: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  progressStep: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  progressPercent: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
  },
  progressBarContainer: {
    width: '100%',
    height: '6px',
    backgroundColor: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#2563EB',
    transition: 'width 0.4s ease-in-out',
    borderRadius: '3px',
  },
  viewOnlyBadge: {
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
  },
  waitingBtn: {
    backgroundColor: '#F59E0B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'not-allowed',
    opacity: 0.8,
  },
  confirmationOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  confirmationModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '550px',
    margin: '20px',
  },
  confirmationHeader: {
    padding: '24px 24px 0 24px',
  },
  confirmationTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  confirmationContent: {
    padding: '16px 24px',
  },
  confirmationText: {
    margin: 0,
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.6,
  },
  confirmationFooter: {
    padding: '16px 24px 24px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    flexWrap: 'wrap',
  },
  confirmationFooterThreeButtons: {
    justifyContent: 'space-between',
  },
  confirmationCancelBtn: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  confirmationAcceptBtn: {
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#059669',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  confirmationDeclineBtn: {
    backgroundColor: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#DC2626',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  confirmationCommentBtn: {
    backgroundColor: '#F59E0B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#D97706',
    },
  },
})

export default AcceptWorkOrderModal
